import { useForm } from 'react-hook-form';
import { useAuth } from '../authentication/hooks/auth';
import axios from '@/lib/axios';
import { useState } from 'react';
import ProfileImageUpload from './ProfileImageUpload';
import { toast } from 'react-toastify';
import { API_PROFILE_UPDATE } from '@/constant/apiRout';
type ProfileFormData = {
  name: string;
};

type PasswordFormData = {
  current_password: string;
  password: string;
  password_confirmation: string;
};

const AccountDetails = () => {
  const { user, mutate } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profileMessage, setProfileMessage] = useState<{
    text: string;
    type: string;
  }>({ text: '', type: '' });
  const [passwordMessage, setPasswordMessage] = useState<{
    text: string;
    type: string;
  }>({ text: '', type: '' });

  // --- Profile Form ---
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isSubmitting: profileLoading },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.data?.name || '',
    },
  });

  const onSubmitProfile = async (data: ProfileFormData) => {
    setProfileMessage({ text: '', type: '' });

    try {
      // Call update API only if name is changed
      if (data.name !== user?.data?.name) {
        const res = await axios.post(API_PROFILE_UPDATE, data);

        if (res.data.result) {
          toast.success('Profile info updated successfully');
          setProfileMessage({
            text: 'Profile updated successfully!',
            type: 'success',
          });
        } else {
          toast.error(res.data.message || 'Profile update failed');
        }
      } else {
        setProfileMessage({
          text: 'No changes detected',
          type: 'info',
        });
      }
    } catch (error: any) {
      setProfileMessage({
        text: error.response?.data?.message || 'Error updating profile',
        type: 'danger',
      });
    }
  };

  // --- Password Form ---
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors, isSubmitting: passwordLoading },
    reset: resetPassword,
    watch,
  } = useForm<PasswordFormData>();

  const onSubmitPassword = async (data: PasswordFormData) => {
    setPasswordMessage({ text: '', type: '' });

    if (data.password !== data.password_confirmation) {
      setPasswordMessage({ text: 'Passwords do not match', type: 'danger' });
      return;
    }

    try {
      const res = await axios.post(API_PROFILE_UPDATE, {
        ...(user?.data?.has_password && {
          current_password: data.current_password,
        }),
        password: data.password,
      });

      if (res.data.result) {
        setPasswordMessage({
          text: res.data.message || 'Password updated successfully!',
          type: 'success',
        });
        resetPassword();
      } else {
        setPasswordMessage({
          text: res.data.message || 'Password update failed',
          type: 'danger',
        });
      }
    } catch (error: any) {
      setPasswordMessage({
        text: error.response?.data?.message || 'Error updating password',
        type: 'danger',
      });
    }
  };
  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select an image first!');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('filename', selectedFile.name);

      const res = await axios.post('/api/v2/profile/update-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.result) {
        mutate(
          {
            ...user,
            data: {
              ...user?.data,
              avatar: res.data?.path,
            },
          },
          false
        );
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Image upload failed');
        throw new Error('Image upload failed');
      }
    } catch (err: any) {
      toast.error('Image upload failed');
      throw new Error('Image upload failed');
    }
  };
  return (
    <div className="dashboard__content">
      <div className="dashboard__content-header">
        <h5>My Account</h5>
      </div>

      <div className="dashboard__content-info">
        <div className="account-pill">
          {/* Profile Message */}
          {profileMessage.text && (
            <div className={`alert alert-${profileMessage.type}`}>
              {profileMessage.text}
            </div>
          )}

          <div className="bg-white rounded mb-4">
            <div className="py-3 border-bottom">
              <h4 className="mb-0 px-4 fw-medium">Update Profile</h4>
            </div>
            <form
              onSubmit={handleProfileSubmit(onSubmitProfile)}
              className="account__form needs-validation p-4"
            >
              <div className="row g-3">
                <ProfileImageUpload
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  image={user?.data?.avatar || ''}
                />

                <div className="col-12">
                  <label htmlFor="name" className="form-label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className={`form-control ${profileErrors.name ? 'is-invalid' : ''}`}
                    {...registerProfile('name', {
                      required: 'Name is required',
                    })}
                    placeholder="Enter your name"
                    disabled={profileLoading}
                  />
                  {profileErrors.name && (
                    <div className="invalid-feedback">
                      {profileErrors.name.message}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label htmlFor="email" className="form-label">
                    Phone Number / Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="email"
                    value={user?.data?.email || ''}
                    readOnly
                    disabled
                  />
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="trk-btn trk-btn--primary mt-2"
                    disabled={profileLoading}
                  >
                    {profileLoading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Password Message */}
          {passwordMessage.text && (
            <div className={`alert alert-${passwordMessage.type}`}>
              {passwordMessage.text}
            </div>
          )}

          <div className="bg-white rounded">
            <div className="py-3 border-bottom">
              <h4 className="mb-0 px-4 fw-medium">Update Password</h4>
            </div>
            <form
              onSubmit={handlePasswordSubmit(onSubmitPassword)}
              className="account__form needs-validation p-4"
            >
              <div className="row g-3">
                {user?.data?.has_password && (
                  <div className="col-12">
                    <label htmlFor="current_password" className="form-label">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="current_password"
                      className={`form-control ${passwordErrors.current_password ? 'is-invalid' : ''}`}
                      {...registerPassword('current_password', {
                        required: user?.data?.has_password
                          ? 'Current password is required'
                          : false,
                      })}
                      placeholder="Current password"
                      disabled={passwordLoading}
                    />
                    {passwordErrors.current_password && (
                      <div className="invalid-feedback">
                        {passwordErrors.current_password.message}
                      </div>
                    )}
                  </div>
                )}

                <div className="col-12">
                  <label htmlFor="password" className="form-label">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    className={`form-control ${passwordErrors.password ? 'is-invalid' : ''}`}
                    {...registerPassword('password', {
                      required: 'New password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    placeholder="New password (min 8 characters)"
                    disabled={passwordLoading}
                  />
                  {passwordErrors.password && (
                    <div className="invalid-feedback">
                      {passwordErrors.password.message}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label htmlFor="password_confirmation" className="form-label">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="password_confirmation"
                    className={`form-control ${passwordErrors.password_confirmation ? 'is-invalid' : ''}`}
                    {...registerPassword('password_confirmation', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === watch('password') || 'Passwords do not match',
                    })}
                    placeholder="Re-type new password"
                    disabled={passwordLoading}
                  />
                  {passwordErrors.password_confirmation && (
                    <div className="invalid-feedback">
                      {passwordErrors.password_confirmation.message}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="trk-btn trk-btn--primary mt-2"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
